import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import gettext from '@eb/gettext';
import replace from 'lodash/replace';
import isEmpty from 'lodash/isEmpty';
import isString from 'lodash/isString';
import classNames from 'classnames';
import Waypoint from 'react-waypoint';
import loadable from '@loadable/component';

import { translationPropType } from '@eb/i18n';
import { GRID_TYPE } from '@eb/eds-media-card-content';

import { getCanonicalSearchUrl } from '@eb/redux-destination';

import SeeMore from '../SeeMore';
import EmptyState from '../EmptyState';
import {
    BUCKET_CONFIG_PROPTYPE,
    EVENTS_PROPTYPES,
    SEE_MORE_PROPTYPE,
    EVENT_SEARCH_PROPTYPES,
} from '../../constants/propTypes';
import {
    BEST_OF_CITY_EVENTS,
    FOLLOWED_PROFILES,
    POPULAR_EVENTS,
    LIKED_EVENTS,
    PRIMARY_BUCKET,
} from '../../constants/constants';
import {
    FREE_TAB,
    FOR_YOU,
    ALL_TAB,
    SEASONAL_TAB,
    ONLINE_TAB,
} from '../../constants/tabConfig';
import { BASE_SEASONAL_CONTENT_PROPTYPES } from '../../constants/seasonal';
import { getBucketConfig, getEventsLayout } from '../../utils/feed';

import './EventsBucket.scss';
import './EventsBucketPremium.scss';

const LIST_TYPE = 'list';
const ONLINE_EVENTS = 'Online Events';

const ConsumerCards = loadable(() => import('./ConsumerCards'));

const DESKTOP_DEFAULT_LAYOUT_CONFIG = {
    max: 16,
    bucketClassNames: 'feed-events-bucket__content',
    layout: {
        default: {
            containerClasses: [
                'eds-g-cell eds-g-cell--has-overflow eds-g-cell-6-12 eds-g-cell-mn-4-12 eds-g-cell-lg-3-12 feed__card-cell',
            ],
        },
    },
};

const MOBILE_DEFAULT_LAYOUT_CONFIG = {
    max: 16,
    bucketClassNames: 'feed-events-bucket__content',
    layout: {
        default: {
            containerClasses: [
                'eds-g-cell eds-g-cell--has-overflow eds-g-cell-1-1 feed__card-cell',
            ],
        },
    },
    propsOverwrite: {
        default: {
            type: LIST_TYPE,
        },
        1: {
            type: GRID_TYPE,
        },
        9: {
            type: GRID_TYPE,
        },
    },
};

const LAYOUT_WIDTH_MAP = {
    odd: 792,
    even: 1272,
};

const _isInOddLayout = (windowWidth) =>
    windowWidth >= LAYOUT_WIDTH_MAP.odd && windowWidth < LAYOUT_WIDTH_MAP.even;

const useWindowWidth = () => {
    const [windowWidth, setWindowWidth] = React.useState('null');

    const updateDimensions = React.useCallback(() => {
        setWindowWidth(window.innerWidth);
    }, []);

    React.useLayoutEffect(() => {
        window.addEventListener('resize', updateDimensions);

        return () => window.removeEventListener('resize', updateDimensions);
    }, [updateDimensions]);

    return windowWidth;
};

const SeeMoreOfBucket = (props) => {
    const {
        bucketKey,
        seeMore,
        onSeeMore,
        searchCriteria = {},
        shouldShowSeeMore,
        locationSlug,
        tabKey,
        userId,
        seasonalContent,
    } = props;

    let seeMoreUrl = seeMore;

    if (shouldShowSeeMore) {
        if (isEmpty(seeMore) && searchCriteria && !isEmpty(locationSlug)) {
            let q = '';

            if (tabKey === SEASONAL_TAB) {
                q = get(
                    seasonalContent,
                    'byDate.eventSearchQ',
                    get(seasonalContent, 'byDate.tabName', ''),
                );
                if (!isString(q)) {
                    q = q.toString();
                }
            }

            const { price, tags, dates } = searchCriteria;

            seeMoreUrl = {
                url: getCanonicalSearchUrl({
                    slug: locationSlug,
                    price,
                    tags,
                    dates,
                    q,
                }),
            };
        }

        if (bucketKey === LIKED_EVENTS && tabKey === FOR_YOU) {
            seeMoreUrl = {
                url: `/u/${userId}/favorites/events`,
            };
        }

        if (bucketKey === FOLLOWED_PROFILES && tabKey === FOR_YOU) {
            seeMoreUrl = {
                url: `/u/${userId}/following`,
            };
        }

        if (tabKey === ONLINE_TAB) {
            seeMoreUrl = {
                url: '/d/online/all-events/',
            };
        }
    }

    return (
        <div data-testid="see-more-events">
            <SeeMore
                bucketKey={bucketKey}
                onClick={onSeeMore}
                seeMore={seeMoreUrl}
            />
        </div>
    );
};

SeeMoreOfBucket.propTypes = {
    bucketKey: PropTypes.string,
    seeMore: PropTypes.func,
    onSeeMore: PropTypes.func,
    searchCriteria: EVENT_SEARCH_PROPTYPES,
    shouldShowSeeMore: PropTypes.bool,
    locationSlug: PropTypes.string,
    tabKey: PropTypes.string,
    userId: PropTypes.string,
    seasonalContent: PropTypes.arrayOf(
        PropTypes.shape(BASE_SEASONAL_CONTENT_PROPTYPES),
    ),
};

const BucketHeader = (props) => {
    if (!props.name && !props.subtitle) {
        return null;
    }

    let titleComponent = null;
    let subtitleComponent = null;

    if (props.name) {
        const titleClassNames = classNames(
            'eds-text-color--grey-900',
            'eds-text-hs',
        );

        let fixedName = replace(props.name, '<location>', props.currentPlace);

        if (props.currentPlace === ONLINE_EVENTS && props.isPrimaryBucket) {
            fixedName = gettext('Online Events');
        }

        titleComponent = <span className={titleClassNames}>{fixedName}</span>;
    }

    if (props.subtitle) {
        const fixedSubtitle = replace(
            props.subtitle,
            '<location>',
            props.currentPlace,
        );

        subtitleComponent = (
            <span className="eds-text-bm eds-text-color--grey-600">
                {fixedSubtitle}
            </span>
        );
    }

    const bucketHeaderClassNames = classNames(
        'eds-l-pad-bot-6',
        {
            'eds-l-sm-pad-bot-10 eds-l-sw-pad-bot-8 pad-bot-mn-up': !props.isSpecialBucket,
            'pad-bot-sm-up--premium': props.isSpecialBucket,
        },
        'eds-l-mn-pad-hor-3',
        'eds-l-md-pad-hor-3',
        'eds-l-mw-pad-hor-3',
        'eds-l-ln-pad-hor-3',
        'eds-l-lg-pad-hor-3',
        'eds-l-lw-pad-hor-3',
    );

    let bucketHeader = null;

    if (subtitleComponent || titleComponent) {
        bucketHeader = (
            <div
                className={bucketHeaderClassNames}
                data-testid="feed-bucket-header"
            >
                {titleComponent}
                <div>{subtitleComponent}</div>
            </div>
        );
    }

    return bucketHeader;
};

const EventsBucket = ({
    events = [],
    searchCriteria = {},
    bucketConfig,
    ...props
}) => {
    const [isVisible, setVisible] = React.useState(
        !props.isMobile && !props.shouldUseWaypoints,
    );
    const windowWidth = useWindowWidth(100);
    const isMobile = windowWidth <= 660;

    const bucketClassNames = React.useMemo(() => {
        const bucketClassNamesDesktop = classNames(
            'feed-events-bucket',
            'feed-events-bucket--animated',
            `feed-events--${props.bucketKey}`,
            {
                animate: isVisible,
            },
        );
        const bucketClassNamesMobile = classNames(
            bucketClassNamesDesktop,
            'feed-events-bucket--mobile',
        );

        return isMobile ? bucketClassNamesMobile : bucketClassNamesDesktop;
    }, [isMobile, isVisible, props.bucketKey]);

    const layoutConfig = React.useMemo(() => {
        const overwrite = isMobile
            ? 'overwrite.props.layout.mobile.events'
            : 'overwrite.props.layout.desktop.events';
        const defaultConfig = isMobile
            ? MOBILE_DEFAULT_LAYOUT_CONFIG
            : DESKTOP_DEFAULT_LAYOUT_CONFIG;

        return getBucketConfig({
            bucketConfig: get(bucketConfig, overwrite, {}),
            defaultConfig,
        });
    }, [isMobile, bucketConfig]);

    const eventGroups = React.useMemo(() => {
        if (isMobile) {
            return getEventsLayout({
                events,
                bucketConfig: layoutConfig,
            });
        }

        return getEventsLayout({
            events,
            bucketConfig: layoutConfig,
        });
    }, [isMobile, layoutConfig, events]);

    if (isEmpty(events)) {
        return (
            <EmptyState
                bucketKey={props.bucketKey}
                emptyState={bucketConfig.emptyState}
                isAuthenticated={props.isAuthenticated}
            />
        );
    }

    const consumerCardsProps = {
        followCountVariant: props.followCountVariant,
        bucketKey: props.bucketKey,
        isAuthenticated: props.isAuthenticated,
        isFreeTab: searchCriteria.price === FREE_TAB,
        shouldShowOnboardingEntryPoint: props.shouldShowOnboardingEntryPoint,
        isFirstBucket: !props.shouldUseWaypoints && props.tabKey === ALL_TAB,
        isInOddLayout: _isInOddLayout(windowWidth),
        onClickOnBoardingCard: props.onClickOnBoardingCard,
        onViewEvent: props.onViewEvent,
        shouldShowOnboardingCard: get(
            bucketConfig,
            'shouldShowOnboardingCard',
            false,
        ),
    };

    return (
        <Waypoint
            onEnter={() => setVisible(true)}
            topOffset={isMobile ? '-300px' : '-500px'}
        >
            <div className={bucketClassNames}>
                <BucketHeader
                    name={props.name}
                    subtitle={props.subtitle}
                    currentPlace={props.currentPlace}
                    isPrimaryBucket={props.bucketKey === PRIMARY_BUCKET}
                    isSpecialBucket={[
                        BEST_OF_CITY_EVENTS,
                        POPULAR_EVENTS,
                    ].includes(props.bucketKey)}
                />

                <ConsumerCards
                    {...consumerCardsProps}
                    bucketLayoutConfig={layoutConfig}
                    eventsGroups={eventGroups}
                    cardType="grid"
                />

                <SeeMoreOfBucket
                    bucketKey={props.bucketKey}
                    seeMore={props.seeMore}
                    onSeeMore={props.onSeeMore}
                    searchCriteria={searchCriteria}
                    shouldShowSeeMore={props.shouldShowSeeMore}
                    locationSlug={props.locationSlug}
                    tabKey={props.tabKey}
                    userId={props.userId}
                    seasonalContent={props.seasonalContent}
                />
            </div>
        </Waypoint>
    );
};

EventsBucket.propTypes = {
    onSeeMore: PropTypes.func.isRequired,

    bucketConfig: BUCKET_CONFIG_PROPTYPE,
    currentPlace: PropTypes.string,
    bucketKey: PropTypes.string,
    events: EVENTS_PROPTYPES,
    isAuthenticated: PropTypes.bool,
    isMobile: PropTypes.bool,
    name: translationPropType,
    seeMore: SEE_MORE_PROPTYPE,
    subtitle: translationPropType,
    shouldUseWaypoints: PropTypes.bool,
    shouldShowSeeMore: PropTypes.bool,
    searchCriteria: EVENT_SEARCH_PROPTYPES,
    locationSlug: PropTypes.string,
    shouldShowOnboardingEntryPoint: PropTypes.bool,
    tabKey: PropTypes.string,
    userId: PropTypes.string,
    onClickOnBoardingCard: PropTypes.func,
    seasonalContent: PropTypes.arrayOf(
        PropTypes.shape(BASE_SEASONAL_CONTENT_PROPTYPES),
    ),
};

export default EventsBucket;
